import 'react-immutable-proptypes';
import { makeLocationSearch } from '@dbh/make-api-url';
import withConformsTo from '@dbh/with-conforms-to-for-production-www';
import { createSlice } from '@reduxjs/toolkit';
import { LOCATION_CHANGE, replace, push, syncHistoryWithStore } from 'react-router-redux';
import { emptyHashesInSsrStateUpdatedToBrowserHash, makeSelectHttpRequestToServerSideRendering } from '@dbh/ssr-data-redux';
import { select, put, all, takeLatest } from 'redux-saga/effects';
import { DAEMON } from '@dbh/redux-extra';
import { createSelector } from 'reselect';
import 'prop-types';
import { createSimpleSelector } from '@dbh/reselect-extra';
import { extractQueryParametersFromUrl } from '@dbh/url-query-utils';
import { SEARCH_RESULTS_PAGE_TYPES, isCountryWithForcedEnglishLocale } from '@dbh/admindev-constants';
import { getCountryAndLocaleFromUrl } from '@dbh/urls';
import _noop from 'lodash/noop';
import { createContext, useContext } from 'react';
import { parse } from 'url';
import { selectRouteInternals } from '@dbh/redux-route-internals';

/**
 * Returns the required route parameter for every route, merged with
 * additional route parameters if supplied.
 * @param {Immutable.Map} countryAndLocaleState Contains the current country and
 * locale data.
 * @param {Object?} urlOptions .
 * @param {Immutable.Map?} urlOptions.routeParameters Additional route parameters.
 */const makeRouteParameters=withConformsTo("makeRouteParameters",[],(a,b)=>{// The base, required route parameters for every route (country and locale).
let c=a;// Has the user supplied a non required `urlOptions` Object?
if(!b)return c;// Has the user supplied additional routeParameters in `urlOptions`?
const{routeParameters:d}=b;return d&&(c=c.merge(d)),c});var makeRouteParameters$1 = makeRouteParameters;

/*
 *
 * `adaptLocationChange`: `@dbh/routing-redux`.
 *
 *//**
 * Given the `LOCATION_CHANGE` event and other related data of the previous
 * state, returns the data to merge into the state. @TODO `jest` test.
 * @param {Object} event .
 * @param {Object} previousLocationBeforeTransitions .
 * @param {string?} previousCountry .
 * @param {string?} previousLocale .
 * @return {Object} Returns only the data that we want to merge into the state.
 */const adaptLocationChange=(a,b,c,d)=>{const{country:e,locale:f,countryChanged:g,localeChanged:h,hash:i,landingPageCountry:j,...k}=a.payload;return {locationBeforeTransitions:{...k,// Some selectors compare the `react-router` `hash` (encoded) with the
// `webpagesRoutes` `hash` (non encoded), we decode the former, so that
// the string comparisons work. @example `#dbh/aziendale/dati-società`.
// @see `makeSelectFindWebpageRouteByHash`, `makeSelectCurrentHashPageReference`
// and `makeSelectTopMenuItemNameFromHash`.
hash:decodeURIComponent(i)},country:e,locale:f,previousCountry:c,previousLocale:d,previousLocationBeforeTransitions:b,landingPageCountry:j||c||e}};var adaptLocationChange$1 = adaptLocationChange;

const routeInitialState={locationBeforeTransitions:null},routingSlice=createSlice({name:"routing",initialState:routeInitialState,extraReducers:a=>{a.addCase(LOCATION_CHANGE,(a,b)=>{const c=a.locationBeforeTransitions,d=a.country,e=a.locale,f=adaptLocationChange$1(b,c,d,e);return {...a,...f}}),a.addCase(emptyHashesInSsrStateUpdatedToBrowserHash.type,(a,b)=>{// The browsers by design do not send the `window.location.hash` to the
// servers, so the `previousLocationBeforeTransitions.hash` in the `redux`
// state sent by our server to the browser, will never contain the `hash`.
// Since for analytics purposes we want it to include the `hash` if
// that `hash` is the `URL` field of an `admindev` "Web Page" that is a
// hash page, here we force `hash` in `previousLocationBeforeTransitions`.
// An important effect of this is that the `makeSelectIsFirstPageAfterSsr`
// selector will return `true` if the user landed on a page like this:
// @see {@link https://test.daybreakhotels.com/IT/en-GB#dbh/sign-in}
// That avoids `gtag` page view being fired two times in that case.
const{payload:{hash:c}}=b;return {...a,previousLocationBeforeTransitions:{...a.previousLocationBeforeTransitions,hash:c}}});}});/**
 * IMPORTANT: `React Router` triggers the `LOCATION_CHANGE` with a plain
 * `object` `locationBeforeTransitions` and currently we do not transform it to
 * an `immutable` instance. However, the reducer is initialized with an
 * `immutable` instance of `locationBeforeTransitions` because the app `state`
 * received from server is altogether transformed to `immutable`. It's
 * substituted with a plain `object` by the subsequent `LOCATION_CHANGE` events.
 * Merge route into the global application state.
 * @param {Immutable.Map} state The previous state.
 * @param {Object} event .
 * @return {Immutable.Map} The next state.
 */var reducer = routingSlice.reducer;

/*
 *
 * Constants: `@dbh/routing-redux`.
 *
 */const PACKAGE_NAME="routing-redux";const REDUX_AND_SAGA_KEY="route";const LOCATION_CHANGE_WITH_SEARCH_CONTEXT="routing-redux/LOCATION_CHANGE_WITH_SEARCH_CONTEXT";const LOCATION_CHANGED_TO_SAME_LOCATION_WITHOUT_HASH="routing-redux/LOCATION_CHANGED_TO_SAME_LOCATION_WITHOUT_HASH";const LOCATION_CHANGED_TO_PREVIOUS_LOCATION="routing-redux/LOCATION_CHANGED_TO_PREVIOUS_LOCATION";const CACHES_CLEARED_FOR_PAGES_EXCLUDING="routing-redux/CACHES_CLEARED_FOR_PAGES_EXCLUDING";

const selectRouteState=a=>a[REDUX_AND_SAGA_KEY];var selectRouteState$1 = selectRouteState;

/**
 * Returns a selector that returns the router state.
 * Expects a plain `JavaScript` `Object` for the routing state.
 * @return {Object} .
 */const makeSelectCurrentRouterState=()=>selectRouteState$1;var makeSelectCurrentRouterState$1 = makeSelectCurrentRouterState;

const makeSelectLocationSearch=()=>createSimpleSelector(selectRouteState$1,["locationBeforeTransitions","search"]);var makeSelectLocationSearch$1 = makeSelectLocationSearch;

const makeSelectLocationPathname=()=>createSimpleSelector(makeSelectCurrentRouterState$1(),["locationBeforeTransitions","pathname"]);var makeSelectLocationPathname$1 = makeSelectLocationPathname;

const makeSelectLocationHash=()=>createSimpleSelector(makeSelectCurrentRouterState$1(),["locationBeforeTransitions","hash"]);var makeSelectLocationHash$1 = makeSelectLocationHash;

const makeSelectLocationHref=()=>createSelector(makeSelectCurrentRouterState$1(),a=>{if(!a)return;const{locationBeforeTransitions:{pathname:b,search:c,hash:d}}=a,e=parse(b);return e.search=c,e.hash=d,e.format()});var makeSelectLocationHref$1 = makeSelectLocationHref;

const makeSelectLocationHrefWithoutHash=()=>createSelector(makeSelectCurrentRouterState$1(),a=>{if(!a)return;const{locationBeforeTransitions:{pathname:b,search:c}}=a,d=parse(b);return d.search=c,d.format()});var makeSelectLocationHrefWithoutHash$1 = makeSelectLocationHrefWithoutHash;

const makeSelectLocationUrlWithGivenHash=withConformsTo("makeSelectLocationUrlWithGivenHash",[],a=>createSelector(makeSelectLocationPathname$1(),makeSelectLocationSearch$1(),(b,c)=>""+b+c+a));var makeSelectLocationUrlWithGivenHash$1 = makeSelectLocationUrlWithGivenHash;

const makeSelectPreviousLocationPathname=()=>createSimpleSelector(makeSelectCurrentRouterState$1(),["previousLocationBeforeTransitions","pathname"]);var makeSelectPreviousLocationPathname$1 = makeSelectPreviousLocationPathname;

const makeSelectPreviousLocation=()=>createSelector(makeSelectCurrentRouterState$1(),a=>a.previousLocationBeforeTransitions);var makeSelectPreviousLocation$1 = makeSelectPreviousLocation;

const makeSelectCountry=()=>createSimpleSelector(makeSelectCurrentRouterState$1(),["country"]);var makeSelectCountry$1 = makeSelectCountry;

const makeSelectLocale=()=>createSimpleSelector(makeSelectCurrentRouterState$1(),["locale"]);var makeSelectLocale$1 = makeSelectLocale;

/**
 * Returns a selector that returns the locale in the `Iso6391` format,
 * the one used by `react-intl`.
 * @return {string} .
 * @see {@link https://en.wikipedia.org/wiki/List_of_ISO_639-1_codes}
 */const makeSelectLocaleIso6391=()=>createSelector(makeSelectLocale$1(),a=>a?a.slice(0,a.indexOf("-")):void 0);var makeSelectLocaleIso6391$1 = makeSelectLocaleIso6391;

const makeSelectCountryAndLocale=()=>createSelector(makeSelectCurrentRouterState$1(),a=>({locale:a.locale,country:a.country}));var makeSelectCountryAndLocale$1 = makeSelectCountryAndLocale;

const makeSelectQueryParameters=a=>createSelector(makeSelectLocationSearch$1(),b=>extractQueryParametersFromUrl(b,a));var makeSelectQueryParameters$1 = makeSelectQueryParameters;

const makeSelectLocationState=()=>createSimpleSelector(makeSelectCurrentRouterState$1(),["locationBeforeTransitions","state"]);var makeSelectLocationState$1 = makeSelectLocationState;

/**
 * Returns a selector that returns the `PAGE_TYPE` for the current route.
 * @return {string} .
 */const makeSelectReactRouterPageType=()=>createSimpleSelector(selectRouteInternals,["reactRouterPageType"]);var makeSelectReactRouterPageType$1 = makeSelectReactRouterPageType;

/**
 * Returns a selector that returns `isPaginated` value for the current route.
 * @return {boolean} .
 */const makeSelectReactRouterIsPaginatedRoute=()=>createSimpleSelector(selectRouteInternals,["isPaginated"]);var makeSelectReactRouterIsPaginatedRoute$1 = makeSelectReactRouterIsPaginatedRoute;

const makeSelectPreviousLocationHref=()=>createSelector(makeSelectCurrentRouterState$1(),a=>{if(!a)return;const{previousLocationBeforeTransitions:b}=a;// `previousLocationBeforeTransitions` is set on the first `LOCATION_CHANGE`
// in the browser, so the first time this code is executed, it might be
// falsy. @see `@dbh/routing-redux#reducer`.
if(!b)return;const{pathname:c,search:d,hash:e}=b,f=parse(c);return f.search=d,f.hash=e,f.format()});var makeSelectPreviousLocationHref$1 = makeSelectPreviousLocationHref;

const makeSelectPreviousLocationHash=()=>createSimpleSelector(makeSelectCurrentRouterState$1(),["previousLocationBeforeTransitions","hash"]);var makeSelectPreviousLocationHash$1 = makeSelectPreviousLocationHash;

// `makeSelectIsFirstPageAfterSsr`. We want to get the first value;
// for example, when opening entry contents in the same page (hashes) and then
// closing them, or when signing in automatically with google one tap sign in,
// there can be various transitions and the previous value of `locationBefore...`
// can be different than the first value. By storing the first value and
// comparing the subsequent values with it, we avoid bugs and can be sure
// when we are in the landing page.
let firstLocationBeforeTransitions="";const compareLocations=(a,b)=>{let{search:c,pathname:d,hash:e}=a,{search:f,pathname:g,hash:h}=b;if(!(d===g))return !1;if(!(e===h))return !1;const i=c===f||decodeURIComponent(c)===decodeURIComponent(f);return i},makeSelectIsFirstPageAfterSsr=()=>createSelector(makeSelectCurrentRouterState$1(),a=>{const{previousLocationBeforeTransitions:b,locationBeforeTransitions:c}=a;if(!b)// There is no previous location, it must be the landing page.
return firstLocationBeforeTransitions=c,!0;if(!firstLocationBeforeTransitions)return firstLocationBeforeTransitions=b,compareLocations(firstLocationBeforeTransitions,c);// The user has navigated at least one time after landing on the landing
// page. But maybe such user went back to the landing page and is now in it.
// To reproduce, land on page `A`, go to page `B` and maybe `C...D` and
// then return to page `A`; this selector will return `true` then.
const d=compareLocations(firstLocationBeforeTransitions,c);return d});/**
 * Returns a selector that returns `true` if the current page is the "landing
 * page", meaning the "first page" after server side rendering (`SSR`). Note
 * that what we call "hash page" (a page with usually a popup open and an `URL`
 * like `https://test.daybreakhotels.com/IT/en-GB#dbh/sign-in`), are considered
 * "real pages" (just with a "different" `UI`) by this function, and generally
 * by our site.
 * Note that browsers, by design, do not send the hash (like `#dbh/sign-in`) to
 * our `Node` server, so `hash` will always be `undefined` when this function is
 * called in server side rendering.
 * Note that `previousLocationBeforeTransitions.hash` is set to
 * `window.location.hash` as soon as `react` loads in the browser, if we detect
 * that the current `hash` is exactly the `URL` of an `admindev` "Web Page"
 * (useful for analytics and pixels).
 * @return {boolean} .
 */var makeSelectIsFirstPageAfterSsr$1 = makeSelectIsFirstPageAfterSsr;

/**
 * Returns a selector that returns `true` if the user is on the landing page and
 * it was served from cache (@see `@dbh/cloudflare-front-end-cache-worker`).
 * @return {boolean} .
 */const makeSelectIsCachedLandingPage=()=>createSelector(makeSelectIsFirstPageAfterSsr$1(),makeSelectHttpRequestToServerSideRendering(),(a,b)=>a&&!0===b.isCachedResult);var makeSelectIsCachedLandingPage$1 = makeSelectIsCachedLandingPage;

const selectLandingPageCountry=createSimpleSelector(selectRouteState$1,["landingPageCountry"]);var selectLandingPageCountry$1 = selectLandingPageCountry;

/**
 * Location change to the current location but adding the given `searchContext`.
 * @param {Object} options .
 * @param {Immutable.Map} options.searchContext .
 * @param {string?} options.locationPathname .
 * @return {Object} .
 */const locationChangeWithSearchContext=withConformsTo("locationChangeWithSearchContext",[],a=>{let{searchContext:b,locationPathname:c}=a;return {type:LOCATION_CHANGE_WITH_SEARCH_CONTEXT,payload:{searchContext:b,locationPathname:c}}});/**
 * Dispatched when we want to remove the `hash` (if any) from the current `URL`.
 * @return {Object} .
 */const locationChangedToSameLocationWithoutHash=()=>({type:LOCATION_CHANGED_TO_SAME_LOCATION_WITHOUT_HASH});/**
 * Dispatched when we want to navigate the user to the previous `URL`.
 * @param {Object} obj .
 * @param {boolean} obj.useReplace Should it use `react-router` `replace`
 * instead of `push`.
 * @return {Object} .
 */const locationChangedToPreviousLocation=withConformsTo("locationChangedToPreviousLocation",[],a=>{let{useReplace:b}=a;return {type:LOCATION_CHANGED_TO_PREVIOUS_LOCATION,payload:{useReplace:b}}});const locationChangedToPreviousLocationEventPropType=_noop;/**
 * Clears the `cache` of `API` values in the reducers that listen to this event
 * for all pages, excluding the one stored according to the keys in the payload.
 * @param {Object} param .
 * @param {string?} param.country .
 * @param {string?} param.locale .
 * @param {string?} param.pathname .
 * @return {Object} .
 */const cachesClearedForPagesExcluding=withConformsTo("cachesClearedForPagesExcluding",[],a=>{let{country:b,locale:c,pathname:d}=a;return {type:CACHES_CLEARED_FOR_PAGES_EXCLUDING,payload:{country:b,locale:c,pathname:d}}});

/**
 * Performs a `react-router-redux` `LOCATION_CHANGE` to the previous `URL`.
 * @param {Object} event .
 * @param {boolean?} event.payload.useReplace .
 */var handleLocationChangedToPreviousLocation = withConformsTo("handleLocationChangedToPreviousLocation",[],function*(a){const{payload:{useReplace:b}}=a,c=yield select(makeSelectPreviousLocation$1());if(c){const a=b?replace:push;yield put(a(c));}});

/**
 * @typedef {import('@dbh/redux-saga-extra').SagaEventResult} SagaEventResult
 *//**
 * Performs a `react-router-redux` `LOCATION_CHANGE` to the exact current `URL`,
 * but without the hash. The `TopMenuHashPage`, at least for now, are all in
 * popups, so instead of `react-router-redux#push` we use `react-router-redux#replace`,
 * so that if the user presses the back button in the browser, it doesn't go
 * back to the hash page in a popup (weird if the possible cases are not
 * handled well) but goes back to a non hash page.
 * @yields {SagaEventResult?} .
 */function*handlelocationChangedToSameLocationWithoutHash(){const a=yield select(makeSelectCurrentRouterState$1()),{locationBeforeTransitions:b}=a,{hash:c}=b;c&&(yield put(replace({...b,hash:""})));}

/**
 * You probably should use `handleLocationChangedToWebpagesRouteWithSearchContext`,
 * unless the `URL` was returned to you by an `API` and it cannot easily be
 * identified using a `pageType` and or a reference.
 * @param {Object} event .
 */var handleLocationChangeWithSearchContext = withConformsTo("handleLocationChangeWithSearchContext",[],function*(a){const{payload:b}=a,{searchContext:c,locationPathname:d}=b;let e=d;e||(e=yield select(makeSelectLocationPathname$1()));const f=yield select(makeSelectQueryParameters$1({dbhQueryParameterOnly:!0})),g={...f,searchContext:c.toJS()},h=makeLocationSearch(g);// This operation will completely replace the existing (if it's there)
// `searchContext` key in the query parameters.
yield put(push({pathname:e,search:h}));});

const injectedSagaConfiguration={key:REDUX_AND_SAGA_KEY,mode:DAEMON};function*rootSaga(){yield all([takeLatest(LOCATION_CHANGE_WITH_SEARCH_CONTEXT,handleLocationChangeWithSearchContext),takeLatest(LOCATION_CHANGED_TO_SAME_LOCATION_WITHOUT_HASH,handlelocationChangedToSameLocationWithoutHash),takeLatest(LOCATION_CHANGED_TO_PREVIOUS_LOCATION,handleLocationChangedToPreviousLocation)]);}

var index = /*#__PURE__*/Object.freeze({
  __proto__: null,
  injectedSagaConfiguration: injectedSagaConfiguration,
  'default': rootSaga
});

/**
 * Is the provided `pageType` rendered using the `SearchResultPage` template code.
 * @param {string} pageType .
 * @return {boolean} .
 * @see `@dbh/search-results-page`.
 */const isPageWithSearchResultsPageTemplate=a=>SEARCH_RESULTS_PAGE_TYPES.includes(a);var isPageWithSearchResultsPageTemplate$1 = isPageWithSearchResultsPageTemplate;

// is under the non-default key ("routing"), `makeSelectCurrentRouterState`
// must be provided for resolving how to retrieve the route in the state.
const syncHistory=(a,b)=>syncHistoryWithStore(a,b,{selectLocationState:makeSelectCurrentRouterState$1()});var syncHistory$1 = syncHistory;

const COUNTRY_ANDOR_LOCALE_CHANGED=PACKAGE_NAME+"/COUNTRY_ANDOR_LOCALE_CHANGED";

/**
 * Country and (or) locale changed.
 * This event is dispatched by the redux middleware `enhanceLocationChange` just
 * after `LOCATION_CHANGE`. If you want to call an `API` from a saga, you probably
 * want to use `LOCATION_CHANGE` directly, like we do in the `wecontrols` and
 * `webpages` `sagas`.
 * @param {Object} obj .
 * @param {string} obj.url .
 * @param {string} obj.country @example `IT`.
 * @param {string} obj.locale @example `it-IT`.
 * @param {string} obj.previousCountry @example `GB`.
 * @param {string} obj.previousLocale @example `en-GB`.
 * @param {boolean} obj.countryChanged Has country has changed.
 * @param {boolean} obj.localeChanged Has locale has changed.
 * @return {Object} .
 */const countryAndOrLocaleChanged=a=>{let{url:b,country:c,locale:d,previousCountry:e,previousLocale:f,countryChanged:g,localeChanged:h}=a;return {type:COUNTRY_ANDOR_LOCALE_CHANGED,payload:{url:b,country:c,locale:d,previousCountry:e,previousLocale:f,countryChanged:g,localeChanged:h}}};

const locationChangeEventPropType=_noop;

// 1) It "enhances" the `react-router-redux` `LOCATION_CHANGE` event by adding
// some useful country/locale related variables in the payload.
// 2) It dispatches the `countryAndOrLocaleChanged` just after the `LOCATION_CHANGE`
// event.
// @see {@link https://redux.js.org/docs/advanced/Middleware.html}
const locationChangeReduxMiddleware=a=>b=>c=>{const{type:d}=c;if(d!==LOCATION_CHANGE)return b(c);const{payload:e}=c,{pathname:f}=e,g=makeSelectCountryAndLocale$1()(a.getState()),{country:h,locale:i}=g,{country:j,locale:k}=getCountryAndLocaleFromUrl(f),l=h!==j,m=isCountryWithForcedEnglishLocale(j)?"en-GB":k,n=i!==m,o=l||n,p={...e,country:j,locale:m,countryChanged:l,localeChanged:n,countryOrLocaleChanged:o};// Compute the `countryChanged` and `localeChanged` variables.
// Some countries have "forced" en-GB locale.
// We do this comparison after checking for countries with "forced" `en-GB`
// locale; this way we avoid incorrectly setting `localeChanged` to `true`
// for such countries when using the "date changer" in the `HdpPage` or
// navigating paginated pages.
// Create the "enhanced" payload for the current `LOCATION_CHANGE` event.
// Build the `LOCATION_CHANGE` event with the "enhanced" payload.
// Dispatch the "enhanced" `LOCATION_CHANGE` event.
let q=b({type:d/* LOCATION_CHANGE */,payload:p});return o&&(q=b(countryAndOrLocaleChanged({url:f,country:j,locale:m,countryChanged:l,localeChanged:n,previousCountry:h,previousLocale:i}))),q};

const RouterContext=/*#__PURE__*/createContext();

/**
 * Returns `true` if the template code of the current page, is displayed in the
 * front end with the search results page template.
 * @return {boolean} .
 */const useIsPageWithSearchResultsPageTemplate=()=>{const a=useContext(RouterContext),b=a.get("pageType"),c=isPageWithSearchResultsPageTemplate$1(b);return c};var useIsPageWithSearchResultsPageTemplate$1 = useIsPageWithSearchResultsPageTemplate;

const importRoutingSaga=Promise.resolve().then(function () { return index; });// Keep the `export *` at the bottom and the other types of exports above.

export { CACHES_CLEARED_FOR_PAGES_EXCLUDING, COUNTRY_ANDOR_LOCALE_CHANGED, LOCATION_CHANGED_TO_PREVIOUS_LOCATION, LOCATION_CHANGED_TO_SAME_LOCATION_WITHOUT_HASH, LOCATION_CHANGE_WITH_SEARCH_CONTEXT, PACKAGE_NAME, REDUX_AND_SAGA_KEY, RouterContext, cachesClearedForPagesExcluding, importRoutingSaga, isPageWithSearchResultsPageTemplate$1 as isPageWithSearchResultsPageTemplate, locationChangeEventPropType, locationChangeReduxMiddleware, locationChangeWithSearchContext, locationChangedToPreviousLocation, locationChangedToPreviousLocationEventPropType, locationChangedToSameLocationWithoutHash, makeRouteParameters$1 as makeRouteParameters, makeSelectCountry$1 as makeSelectCountry, makeSelectCountryAndLocale$1 as makeSelectCountryAndLocale, makeSelectCurrentRouterState$1 as makeSelectCurrentRouterState, makeSelectIsCachedLandingPage$1 as makeSelectIsCachedLandingPage, makeSelectIsFirstPageAfterSsr$1 as makeSelectIsFirstPageAfterSsr, makeSelectLocale$1 as makeSelectLocale, makeSelectLocaleIso6391$1 as makeSelectLocaleIso6391, makeSelectLocationHash$1 as makeSelectLocationHash, makeSelectLocationHref$1 as makeSelectLocationHref, makeSelectLocationHrefWithoutHash$1 as makeSelectLocationHrefWithoutHash, makeSelectLocationPathname$1 as makeSelectLocationPathname, makeSelectLocationSearch$1 as makeSelectLocationSearch, makeSelectLocationState$1 as makeSelectLocationState, makeSelectLocationUrlWithGivenHash$1 as makeSelectLocationUrlWithGivenHash, makeSelectPreviousLocation$1 as makeSelectPreviousLocation, makeSelectPreviousLocationHash$1 as makeSelectPreviousLocationHash, makeSelectPreviousLocationHref$1 as makeSelectPreviousLocationHref, makeSelectPreviousLocationPathname$1 as makeSelectPreviousLocationPathname, makeSelectQueryParameters$1 as makeSelectQueryParameters, makeSelectReactRouterIsPaginatedRoute$1 as makeSelectReactRouterIsPaginatedRoute, makeSelectReactRouterPageType$1 as makeSelectReactRouterPageType, reducer as routeReducer, rootSaga as routeSaga, selectLandingPageCountry$1 as selectLandingPageCountry, syncHistory$1 as syncHistoryWithStore, useIsPageWithSearchResultsPageTemplate$1 as useIsPageWithSearchResultsPageTemplate };
